import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import {
    CheckCircle, Award, Ship, ChevronRight,
    Calendar, Clock, Headphones, HelpCircle,
    ChevronDown, Shield, Palette, Zap, Sparkles,
    Phone, Mail, MapPin, Map as MapIcon, Facebook, Instagram, Twitter
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

// Import Assets
import heroImage from '../assets/home-hero.png';
import interiorImage from '../assets/interior-hero.png';
import textureImage from '../assets/texture-hero.png';
import woodImage from '../assets/wood-hero.png';

// --- Sub-components ---

const Breadcrumbs = () => (
    <nav className="flex px-4 py-3 text-sm font-medium text-gray-500 bg-white/80 backdrop-blur-md sticky top-20 z-40 border-b border-slate-100" aria-label="Breadcrumb">
        <ol className="max-w-7xl mx-auto w-full flex items-center space-x-2">
            <li><Link to="/" className="hover:text-orange-600 transition-colors">Home</Link></li>
            <li><ChevronRight size={14} /></li>
            <li className="text-blue-900 font-bold">Madurai Painters | Tamil Nadu</li>
        </ol>
    </nav>
);

const AnimatedCounter = ({ value, duration = 2 }) => {
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => Math.round(latest));
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (isInView) {
            animate(count, value, { duration });
        }
    }, [isInView, count, value, duration]);

    return <motion.span ref={ref}>{rounded}</motion.span>;
};

const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border-b border-slate-100 last:border-none">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center py-6 text-left group"
            >
                <span className={`text-lg font-bold transition-colors ${isOpen ? 'text-orange-600' : 'text-blue-900 group-hover:text-orange-600'}`}>{question}</span>
                <ChevronDown className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-orange-600' : 'text-slate-400'}`} />
            </button>
            <motion.div
                initial={false}
                animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
                className="overflow-hidden"
            >
                <p className="pb-6 text-gray-600 leading-relaxed">{answer}</p>
            </motion.div>
        </div>
    );
};

// --- Main Page Component ---

const Home = () => {
    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6 }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <SEO
                title="Top Rated Painters in Madurai | Home Painting Services"
                description="SSS Home Painting provides professional interior, exterior, and texture painting services in Madurai. 16+ years of family legacy. Get a free quote today!"
            />

            <Breadcrumbs />

            {/* Hero Section */}
            <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src={heroImage}
                        alt="Best Painting Service Madurai - SSS Home Painting Luxury Living Room"
                        title="Top Rated Painters in Madurai"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-blue-900/60 backdrop-blur-[1px]"></div>
                </div>

                <div className="relative z-10 text-center px-4 max-w-5xl">
                    <motion.span
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-orange-400 font-bold tracking-[0.3em] uppercase mb-4 block"
                    >
                        Madurai's Most Trusted Painters
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight"
                    >
                        Top Rated Painters in <span className="text-orange-500">Madurai</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-blue-50 mb-10 font-medium italic"
                    >
                        "Bringing Walls to Life with 16+ Years of Family Legacy in Tamil Nadu"
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="flex flex-col sm:flex-row items-center justify-center gap-4"
                    >
                        <Link to="/contact" className="btn-primary">Get Free Consultation</Link>
                        <Link to="/services/interior" className="px-8 py-3 rounded-full font-bold border-2 border-white text-white hover:bg-white hover:text-blue-900 transition-all duration-300 shadow-xl">
                            Our Wall Designs
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Stats Section with Animated Counters */}
            <section className="py-24 bg-white px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                        {[
                            { label: 'Projects Completed in Madurai', value: 500, icon: <CheckCircle className="text-orange-500" /> },
                            { label: 'Cities Covered in Tamil Nadu', value: 50, icon: <Award className="text-orange-500" /> },
                            { label: 'Upcoming Major Projects', value: 20, icon: <Ship className="text-orange-500" /> },
                        ].map((stat, idx) => (
                            <motion.div
                                key={idx}
                                {...fadeInUp}
                                transition={{ delay: idx * 0.2 }}
                                className="flex flex-col items-center p-10 rounded-3xl bg-slate-50 border border-slate-100 hover:shadow-2xl transition-all duration-500 group"
                            >
                                <div className="mb-6 bg-white p-5 rounded-2xl shadow-sm group-hover:bg-orange-600 group-hover:text-white transition-colors duration-300">
                                    {React.cloneElement(stat.icon, { size: 40, className: "group-hover:text-white transition-colors" })}
                                </div>
                                <h3 className="text-6xl font-extrabold text-blue-900 mb-2">
                                    <AnimatedCounter value={stat.value} />+
                                </h3>
                                <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Interactive Tamil Nadu Map / Network Section */}
            <section className="py-24 bg-blue-950 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <motion.div {...fadeInUp} className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Service Network Across <span className="text-orange-500">Tamil Nadu</span></h2>
                        <p className="text-blue-200 text-lg max-w-2xl mx-auto">From our base in Madurai, we provide premium painting services to all major cities in Tamil Nadu.</p>
                    </motion.div>

                    <motion.div
                        {...fadeInUp}
                        className="relative h-[500px] w-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-[3rem] shadow-2xl p-8 flex items-center justify-center overflow-hidden"
                    >
                        {/* Abstract Map Grid */}
                        <div className="absolute inset-0 grid grid-cols-10 grid-rows-10 gap-4 opacity-10">
                            {[...Array(100)].map((_, i) => <div key={i} className="border border-white/20"></div>)}
                        </div>

                        {/* Network Dots */}
                        <div className="relative w-full h-full max-w-2xl mx-auto">
                            {/* Madurai (Center) */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-orange-500 rounded-full animate-ping opacity-75"></div>
                                    <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center border-4 border-white shadow-[0_0_20px_rgba(234,88,12,0.8)] relative z-10"></div>
                                    <span className="absolute top-8 left-1/2 -translate-x-1/2 text-white font-bold text-sm whitespace-nowrap">Madurai (HQ)</span>
                                </div>
                            </div>

                            {/* Chennai */}
                            <div className="absolute top-10 right-20">
                                <MapPin className="text-white animate-bounce" size={24} />
                                <span className="absolute top-8 left-1/2 -translate-x-1/2 text-blue-200 font-semibold text-xs whitespace-nowrap">Chennai</span>
                            </div>

                            {/* Coimbatore */}
                            <div className="absolute top-1/3 left-10">
                                <MapPin className="text-white" size={24} />
                                <span className="absolute top-8 left-1/2 -translate-x-1/2 text-blue-200 font-semibold text-xs whitespace-nowrap">Coimbatore</span>
                            </div>

                            {/* Trichy */}
                            <div className="absolute top-1/2 right-1/4">
                                <MapPin className="text-white" size={24} />
                                <span className="absolute top-8 left-1/2 -translate-x-1/2 text-blue-200 font-semibold text-xs whitespace-nowrap">Trichy</span>
                            </div>

                            {/* Network Lines (SVG) */}
                            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
                                <line x1="50%" y1="50%" x2="75%" y2="10%" stroke="orange" strokeWidth="2" strokeDasharray="5,5" />
                                <line x1="50%" y1="50%" x2="10%" y2="35%" stroke="orange" strokeWidth="2" strokeDasharray="5,5" />
                                <line x1="50%" y1="50%" x2="70%" y2="55%" stroke="orange" strokeWidth="2" strokeDasharray="5,5" />
                            </svg>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Service Process Section */}
            <section className="py-24 bg-white px-4">
                <div className="max-w-7xl mx-auto">
                    <motion.h2 {...fadeInUp} className="section-title">Our Professional Process</motion.h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {[
                            { title: "Consultation", icon: <Headphones size={32} />, desc: "Expert assessment of your walls in Madurai." },
                            { title: "Color Choice", icon: <Palette size={32} />, desc: "Choosing the perfect shades for your home." },
                            { title: "Expert Painting", icon: <Sparkles size={32} />, desc: "Premium application by our skilled team." },
                            { title: "Final Delivery", icon: <Zap size={32} />, desc: "A clean, beautiful home ready for you." },
                        ].map((step, i) => (
                            <motion.div
                                key={i}
                                {...fadeInUp}
                                transition={{ delay: i * 0.1 }}
                                className="relative flex flex-col items-center text-center p-8 rounded-3xl bg-slate-50 border border-slate-100 group hover:bg-blue-900 transition-all duration-500"
                            >
                                <div className="mb-6 text-orange-600 group-hover:text-white transition-colors">{step.icon}</div>
                                <h3 className="text-xl font-bold text-blue-900 mb-3 group-hover:text-white transition-colors">{step.title}</h3>
                                <p className="text-gray-500 text-sm group-hover:text-blue-100 transition-colors uppercase tracking-tight">{step.desc}</p>
                                {i < 3 && <ChevronRight className="hidden md:block absolute -right-5 top-1/2 -translate-y-1/2 text-slate-200 group-hover:text-orange-500" size={32} />}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Working Hours & Availability */}
            <section className="py-20 bg-slate-50 px-4">
                <div className="max-w-5xl mx-auto bg-white rounded-[3rem] shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2">
                    <div className="bg-blue-900 p-12 text-white">
                        <h3 className="text-3xl font-bold mb-8">Working Hours</h3>
                        <div className="space-y-6">
                            <div className="flex justify-between border-b border-blue-800 pb-2">
                                <span className="font-semibold">Monday - Saturday</span>
                                <span>9:00 AM - 8:00 PM</span>
                            </div>
                            <div className="flex justify-between border-b border-blue-800 pb-2">
                                <span className="font-semibold">Sunday</span>
                                <span className="text-orange-500 font-bold tracking-wider">Closed</span>
                            </div>
                        </div>
                        <div className="mt-12 flex items-center space-x-4 bg-blue-800/50 p-6 rounded-2xl border border-blue-700">
                            <Clock className="text-orange-500" />
                            <p className="font-bold">Available 24/7 for Online Inquiries via WhatsApp</p>
                        </div>
                    </div>
                    <div className="p-12 flex flex-col justify-center">
                        <h3 className="text-2xl font-bold text-blue-900 mb-6">Immediate Support</h3>
                        <p className="text-gray-600 mb-8">Have a painting project in mind? Call us directly or send a message for a free estimate within 24 hours.</p>
                        <div className="space-y-4">
                            <a href="tel:+918072127022" className="flex items-center space-x-4 p-4 bg-slate-50 rounded-xl hover:bg-orange-50 transition-colors border border-slate-100">
                                <Phone className="text-orange-600" />
                                <span className="font-bold text-blue-900">+91 80721 27022</span>
                            </a>
                            <a href="mailto:pastranshiyan11@gmail.com" className="flex items-center space-x-4 p-4 bg-slate-50 rounded-xl hover:bg-orange-50 transition-colors border border-slate-100">
                                <Mail className="text-orange-600" />
                                <span className="font-bold text-blue-900">pastranshiyan11@gmail.com</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Painting Tips Section */}
            <section className="py-24 bg-white px-4">
                <div className="max-w-7xl mx-auto">
                    <motion.h2 {...fadeInUp} className="section-title">Maintenance & Painting Tips</motion.h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { title: "Daily Dusting", desc: "Use a clean, dry microfiber cloth to gently dust your interior walls to maintain that 'Top Rated' fresh look.", img: interiorImage },
                            { title: "Moisture Check", desc: "Always check for dampness before painting. Our Madurai experts specialize in waterproofing for long-lasting results.", img: textureImage },
                            { title: "Touch-up Secrets", desc: "Keep a small amount of the original paint for minor touch-ups. Ensure the surface is clean before applying.", img: woodImage }
                        ].map((tip, i) => (
                            <motion.div
                                key={i}
                                {...fadeInUp}
                                whileHover={{ y: -10 }}
                                className="bg-slate-50 rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-sm"
                            >
                                <img
                                    src={tip.img}
                                    alt={`SSS Home Painting Tip - ${tip.title} for Madurai Homes`}
                                    className="h-56 w-full object-cover"
                                />
                                <div className="p-8">
                                    <h4 className="text-xl font-bold text-blue-900 mb-4">{tip.title}</h4>
                                    <p className="text-gray-600 text-sm leading-relaxed">{tip.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 bg-slate-50 px-4">
                <div className="max-w-4xl mx-auto">
                    <motion.div {...fadeInUp} className="text-center mb-16">
                        <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                            <HelpCircle className="text-orange-600" size={32} />
                        </div>
                        <h2 className="text-4xl font-bold text-blue-900 mb-4 uppercase tracking-tighter">Frequently Asked Questions</h2>
                        <p className="text-gray-500">Everything you need to know about our painting services in Madurai.</p>
                    </motion.div>

                    <motion.div {...fadeInUp} className="bg-white p-8 md:p-12 rounded-[3rem] shadow-xl border border-slate-100">
                        <FAQItem
                            question="What is the average duration of a painting project in Madurai?"
                            answer="A typical 2BHK interior project takes about 5-7 days, including surface preparation. Exterior projects may take 10-15 days depending on the weather conditions in Madurai."
                        />
                        <FAQItem
                            question="Do you provide a warranty for exterior painting?"
                            answer="Yes, we provide up to 5-7 years of warranty for premium exterior painting against peeling and fungal growth, specifically formulated for Tamil Nadu weather."
                        />
                        <FAQItem
                            question="Which brand of paint do you use?"
                            answer="We primarily use top-tier brands like Nippon, Asian Paints, Berger, and Dulux. Based on your budget and preference, we suggest the best variant from these brands."
                        />
                        <FAQItem
                            question="Is it necessary to use wall putty for full repainting?"
                            answer="For a professional, 'Top Rated' finish, we recommend at least one coat of putty on uneven areas. For new buildings, two coats are mandatory."
                        />
                        <FAQItem
                            question="Do you provide waterproofing along with painting?"
                            answer="Absolutely! We are experts in terrace and wall waterproofing. We ensure the source of dampness is fixed before we start the painting work."
                        />
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Home;